import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import { useRef, useEffect } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import Image from "next/image";

export default function GenericSwiper({
  items = [],
  hasArrows = false,
  arrowColor,
  CardComponent,
  slidesPerView = "auto",
  spaceBetween = 15,
  showPagination = false,
  showNavigation = true,
  autoplayDelay = 3000,
  speed,
  cardWidth = 400,
  sx = {},
  locale = "en",
  cardClassName = "",
  ...rest
}) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // swiper instance
  const swiperRef = useRef(null);

  // arrows refs
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const isAr = locale === "ar";
  const dir = locale === "en" ? "left" : "right";

  // EN: left = prev, right = next
  // AR: right = prev, left = next
  const prevElRef = isAr ? rightRef : leftRef;
  const nextElRef = isAr ? leftRef : rightRef;

  // IMPORTANT FIX: re-init navigation after refs exist
  useEffect(() => {
    if (!hasArrows) return;
    const swiper = swiperRef.current;
    if (!swiper) return;

    if (!prevElRef.current || !nextElRef.current) return;

    swiper.params.navigation.prevEl = prevElRef.current;
    swiper.params.navigation.nextEl = nextElRef.current;

    swiper.navigation.destroy();
    swiper.navigation.init();
    swiper.navigation.update();
  }, [hasArrows, locale]);

  // mobile fallback
  if (isSmallScreen && rest.type !== "details") {
    return (
      <Box display="flex" flexDirection="column" gap={2} alignItems="center">
        {items?.map((item, idx) =>
          CardComponent ? (
            <CardComponent key={item.id || idx} {...item} />
          ) : null,
        )}
      </Box>
    );
  }

  const defaultSwiperArrowsStyles = {
    "& .swiper-button-next": {
      backgroundImage:
        locale === "en"
          ? "url('/forward1-arrow.png')"
          : "url('/back1-arrow.png')",
      backgroundSize: "100% auto",
      backgroundPosition: "center",
      right: { xs: "10px !important", sm: "20px !important" },
      left: "auto !important",
      top: "100%",
      width: "40px !important",
      height: "40px !important",
    },
    "& .swiper-button-prev": {
      backgroundImage:
        locale === "en"
          ? "url('/back1-arrow.png')"
          : "url('/forward1-arrow.png')",
      backgroundSize: "100% auto",
      backgroundPosition: "center",
      right: { xs: "60px !important", sm: "70px !important" },
      left: "auto !important",
      top: "100%",
      width: "40px !important",
      height: "40px !important",
    },
    "& .swiper-button-next::after": { display: "none" },
    "& .swiper-button-prev::after": { display: "none" },
  };

  return (
    <Box
      dir={isAr ? "rtl" : "ltr"}
      sx={{
        p: rest.type !== "details" ? 4 : 1,
        width: "100%",

        "& .swiper-pagination": {
          textAlign: dir,
          minHeight: "30px",
          overflow: "visible",
        },
        "& .swiper-pagination-bullet": {
          bgcolor: "#000",
          display: { xs: "none" },
        },
        "& .swiper-pagination-bullet-active": {
          bgcolor: "#F18222",
        },
        "& .swiper": {
          overflow: "unset",
        },

        ...(hasArrows
          ? {
              "& .swiper-button-prev, & .swiper-button-next": {
                display: "none !important",
              },
            }
          : defaultSwiperArrowsStyles),

        ...sx,
      }}
    >
      <Swiper
        key={locale}
        dir={isAr ? "rtl" : "ltr"}
        slidesPerView={slidesPerView}
        loop
        speed={speed}
        spaceBetween={spaceBetween}
        breakpointsBase="window"
        pagination={showPagination ? { clickable: true } : false}
        navigation={
          hasArrows
            ? { prevEl: prevElRef.current, nextEl: nextElRef.current }
            : showNavigation
        }
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onBeforeInit={(swiper) => {
          if (hasArrows) {
            swiper.params.navigation.prevEl = prevElRef.current;
            swiper.params.navigation.nextEl = nextElRef.current;
          }
        }}
        modules={[Pagination, Navigation, Autoplay]}
        autoplay={{
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
          stopOnLastSlide: false,
        }}
        style={{
          paddingBottom: "50px",
          paddingTop: rest.withSpace ? "180px" : "0px",
        }}
        {...rest}
      >
        {items?.map((item, idx) => (
          <SwiperSlide
            key={item.id || idx}
            style={{ width: "fit-content", flexShrink: 0 }}
          >
            {CardComponent ? <CardComponent {...item} /> : null}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom arrows */}
      {hasArrows && (
        <Box
          dir="ltr"
          sx={{
            display: "flex",
            gap: 5,
            justifyContent: "center",
            alignItems: "center",
            mt: 1,
          }}
        >
          {/* LEFT */}
          <IconButton
            ref={leftRef}
            aria-label="left"
            sx={{
              width: 44,
              height: 44,
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <Image
              src="/left-arrow.svg"
              width={60}
              height={60}
              alt="left-arrow"
            />
          </IconButton>

          {/* RIGHT */}
          <IconButton
            ref={rightRef}
            aria-label="right"
            sx={{
              width: 44,
              height: 44,
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <Image
              src="/right-arrow.svg"
              width={60}
              height={60}
              alt="right-arrow"
            />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
